import { useState } from 'react';
import { X, Check, Plane } from '@/components/icons';
import { Button } from './ui/button';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from './ui/drawer';
import { useAircraftGetAll } from '../lib/hooks';
import { ListSkeleton } from './ui/skeleton-presets';
import { SearchInput } from './ui/search-input';
import { cn } from '@/lib/utils';
import { EmptyState } from './EmptyState';
import { AircraftDrawer } from './AircraftDrawer';

interface AircraftPickerDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (aircraftId: string) => void;
  selectedAircraftId?: string;
  title?: string;
  // When true, aircraft in maintenance are selectable (e.g. picking an aircraft
  // for a trip, which has no date — the per-flight date check enforces windows).
  allowMaintenance?: boolean;
}

export function AircraftPickerDrawer({
  isOpen,
  onClose,
  onSelect,
  selectedAircraftId,
  title = 'Select Aircraft',
  allowMaintenance = false,
}: AircraftPickerDrawerProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [isAddAircraftOpen, setIsAddAircraftOpen] = useState(false);

  // Use hooks for data
  const { data: allAircraft = [], isPending: loading } = useAircraftGetAll();

  const filteredAircraft = allAircraft.filter((plane) =>
    (plane.registration || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (plane.type || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleClose = () => {
    setSearchQuery('');
    onClose();
  };

  return (
    <>
    <Drawer open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DrawerContent className="bg-surface-L0 rounded-t-[10px] max-h-[85dvh]">
        <DrawerHeader className="flex items-center justify-between p-4 border-b border-border-L2">
          <DrawerTitle className="text-lg font-semibold text-content-primary-bold">
            {title}
          </DrawerTitle>
          <Button variant="secondary" size="icon" roundness="round" className="size-8" onClick={handleClose}>
            <X className="w-4 h-4" />
          </Button>
        </DrawerHeader>

        {/* Content */}
        <div className="flex-1 overflow-hidden px-4 pt-4">
          <div className="flex flex-col gap-2 h-full">
            {/* Search Input */}
            <SearchInput
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search tail number"
            />

            {/* Aircraft List */}
            <div className="flex-1 overflow-y-auto overscroll-contain" style={{ WebkitOverflowScrolling: 'touch' }}>
              {loading ? (
                <ListSkeleton />
              ) : filteredAircraft.length === 0 ? (
                searchQuery ? (
                  <div className="flex flex-col items-center justify-center py-12 px-4">
                    <p className="text-sm text-content-primary-subtle text-center">
                      No aircraft found matching your search
                    </p>
                  </div>
                ) : (
                  <EmptyState
                    icon={Plane}
                    title="No Aircraft"
                    description="Add an aircraft to assign it here."
                    addButtonText="Add Aircraft"
                    onAdd={() => setIsAddAircraftOpen(true)}
                  />
                )
              ) : (
                <div className="flex flex-col gap-2 max-h-[300px] overflow-y-auto">
                  {filteredAircraft.map((plane) => {
                    const isSelected = selectedAircraftId === plane.id;
                    const inMaintenance = plane.aircraftStatus === 'Maintenance';
                    // Active is always selectable; Maintenance only when allowed.
                    // Inactive is never selectable.
                    const isSelectable =
                      plane.aircraftStatus === 'Active' ||
                      (allowMaintenance && inMaintenance);
                    const isDisabled = !isSelectable;

                    return (
                      <button
                        key={plane.id}
                        onClick={() => {
                          if (!isSelectable) return;
                          onSelect(plane.id);
                          handleClose();
                        }}
                        disabled={isDisabled}
                        className={cn(
                          'w-full flex items-center justify-between gap-3 min-h-9 px-3 py-[7.5px] rounded-md transition-colors',
                          isDisabled
                            ? 'opacity-50 cursor-not-allowed'
                            : isSelected
                              ? 'bg-surface-L1'
                              : 'active:bg-surface-L2'
                        )}
                      >
                        {/* Aircraft Info */}
                        <div className="flex flex-col items-start min-w-0">
                          <p className={cn(
                            'text-sm font-semibold leading-5 text-left',
                            isDisabled ? 'text-content-primary-subtle' : 'text-content-primary-bold'
                          )}>
                            {plane.registration}
                          </p>
                          <p className="text-xs leading-4 text-content-primary-subtle text-left">
                            {plane.type}
                            {inMaintenance && (
                              <span className="text-background-notice-bold">
                                {plane.type ? ' · ' : ''}In maintenance
                              </span>
                            )}
                          </p>
                        </div>

                        {/* Checkmark when selected */}
                        {isSelected && !isDisabled && (
                          <Check className="size-5 text-content-primary-bold shrink-0" />
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </DrawerContent>
    </Drawer>

    {/* Add Aircraft Drawer — opens from the empty state */}
    <AircraftDrawer
      isOpen={isAddAircraftOpen}
      onClose={() => setIsAddAircraftOpen(false)}
    />
    </>
  );
}
